import React, { Fragment, useState, useEffect } from "react"
import TicketCard from "./TicketCard"
import TicketDetailCard from "../../../Ticket/TicketDetailCard"
import { useLang } from "hooks/useLang"
import ToggleButtons from "./ToggleButtons"
import NoTicketsMessage from "./NoTicketsMessage"
import { EventDate } from "types/event"
import { EventMedia, Image } from "types/event-media"
import { getEventPosterUrl } from "components/atoms/Image/EventPoster"

interface IProps {
    tickets: UserTicketsResponse["payload"]
    isSuccess: boolean
}

interface UserTicketsResponse {
    message: string
    payload: {
        past: UserTickets[]
        current: UserTickets[]
    }
}

export interface UserTicket {
    _id: string
    price: number
    status: string
    token: string
    checked_in_at: Date
    start_time: Date
    end_time: Date
    title: string
    ticket_number: string
    pdf_url: string
    seat_id: string
}

interface UserTickets {
    event_id: string
    en_event_name: string
    ar_event_name: string
    event_date: EventDate
    event_city: string
    event_posters: string[]
    tickets: UserTicket[]
    event?: any
    event_media_posters: {
        card: Image
        hero: Image
        gallery: Image[]
        web_poster: Image
        mobile_poster: Image
    }
}

const TicketTab = ({ tickets, isSuccess }: IProps) => {
    const [state, setState] = useState({
        tickets: tickets?.current || [],
        tab: "current",
        isOpen: false,
    })

    const { isAr } = useLang()
    const [selectedEvent, setSelectedEvent] = useState<UserTickets>()

    const handleOpen = (event_id: string) => {
        setState((prev) => ({ ...prev, isOpen: true }))
        if (state.tab === "past") {
            setSelectedEvent(
                tickets.past.find(
                    (item: UserTickets) => item.event_id === event_id
                )
            )
        }
        if (state.tab === "current") {
            setSelectedEvent(
                tickets.current.find(
                    (item: UserTickets) => item.event_id === event_id
                )
            )
        }
    }

    useEffect(() => {
        if (isSuccess) {
            setState((prev) => ({ ...prev, tickets: tickets.current }))
        }
    }, [isSuccess, tickets])

    return (
        <div className="flex flex-col gap-5">
            <ToggleButtons
                tab={state.tab}
                setCurrent={() => {
                    setState({
                        tab: "current",
                        tickets: tickets?.current,
                        isOpen: false,
                    })
                }}
                setPast={() => {
                    setState({
                        tab: "past",
                        tickets: tickets?.past,
                        isOpen: false,
                    })
                }}
            />

            {state.tickets.length === 0 ? (
                <NoTicketsMessage
                    messageKey={
                        state.tab === "current"
                            ? "no_current_tickets"
                            : "no_past_tickets"
                    }
                />
            ) : (
                <div className="grid grid-cols-1 gap-5 2md:grid-cols-2">
                    {state.tickets.map((item: UserTickets, index: number) => {
                        const {
                            ar_event_name,
                            en_event_name,
                            event_city,
                            event_id,
                            event_posters,
                            tickets,
                            event_media_posters,
                            event,
                        } = item

                        // Skip rendering if tickets array is undefined or empty
                        if (
                            !tickets ||
                            !Array.isArray(tickets) ||
                            tickets.length === 0
                        ) {
                            return null
                        }

                        // console.log({ event_media_posters, item, event })
                        const mainImage = getEventPosterUrl(event, "desktop")
                        const event_title = isAr ? ar_event_name : en_event_name
                        // console.log({ mainImage })
                        return (
                            <Fragment key={index}>
                                {!state.isOpen && (
                                    <TicketCard
                                        title={event_title}
                                        numberOfTickets={tickets.length}
                                        poster={mainImage}
                                        event_id={event_id}
                                        TransactionDate={tickets[0].start_time.toString()}
                                        city={event_city}
                                        onOpen={() => handleOpen(event_id)}
                                        isPast={state.tab === "past"}
                                    />
                                )}
                            </Fragment>
                        )
                    })}
                </div>
            )}

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
                {/* Sorted to show the last buyed tickets first */}
                {selectedEvent?.tickets
                    .sort(function compare(
                        a: { end_time: string | number | Date },
                        b: { end_time: string | number | Date }
                    ) {
                        // TODO: extract to helper function
                        const dateA = new Date(a.end_time)
                        const dateB = new Date(b.end_time)
                        return +dateB - +dateA
                    })
                    .map((ticketItem: UserTicket, i: number) => {
                        const {
                            end_time,
                            _id,
                            ticket_number,
                            price,
                            start_time,
                            status,
                            title,
                            token,
                            seat_id,
                            pdf_url,
                        } = ticketItem
                        const mainImage = getEventPosterUrl(
                            selectedEvent.event,
                            "desktop"
                        )
                        // console.log({ ticketItem, selectedEvent })
                        return (
                            <Fragment key={_id}>
                                {state.isOpen && (
                                    <TicketDetailCard
                                        key={_id}
                                        ticket_end_time={end_time.toString()}
                                        ticket_id={_id}
                                        ticket_number={ticket_number}
                                        ticket_price={price}
                                        ticket_start_time={start_time.toString()}
                                        ticket_status={status}
                                        ticket_title={title}
                                        ticket_token={token}
                                        event_city={selectedEvent.event_city}
                                        poster={mainImage}
                                        event_title={
                                            isAr
                                                ? selectedEvent.ar_event_name
                                                : selectedEvent.en_event_name
                                        }
                                        seat_id={seat_id}
                                        index={i + 1}
                                        lastIndex={selectedEvent.tickets.length}
                                        isPast={state.tab === "past"}
                                        pdf_url={pdf_url}
                                    />
                                )}
                            </Fragment>
                        )
                    })}
            </div>
        </div>
    )
}

export default TicketTab
